/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from "react";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage, IntlShape } from "react-intl";

import "../../common/QuotesDetail/quoteColumns.scss";
import Badge from "components/atoms/Badge/Badge";
import { formatDatetime, formatPriceV2 } from "helpers/commonHelpers";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import { ReactComponent as ArrowDown } from "assets/images/icons/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/images/icons/ArrowUp.svg";
import { ReactComponent as RateCardIcon } from "assets/images/icons/RateCard.svg";
import { ReactComponent as WarningIcon } from "assets/images/icons/Warning.svg";
import { ReactComponent as ApiIcon } from "assets/images/icons/Api.svg";
import { ReactComponent as FolderIcon } from "assets/images/icons/Folder.svg";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as FileIcon } from "assets/images/icons/File.svg";
import QuotationAction from "./QuotationAction";
import QuotationStatus from "./QuotationStatus";
import { IShipmentData } from "./QuotationType";

const renderColor = (value: any) => {
  if (!value) return "black";
  const onTime = parseFloat(value);
  if (onTime >= 0.9) return "#9ec728";
  if (onTime < 0.9 && onTime >= 0.8) return "#ff9000";
  if (onTime < 0.8) return "#EA432D";
};

function formatFlightTransits(array: any) {
  const combinedArray: any = [];
  let previousObject: any = null;

  array.forEach((object: any, index: any) => {
    if (index === 0 || object.label !== previousObject.label) {
      combinedArray.push(object);
    } else {
      const combinedObject = {
        label: object.label,
        departureTime: previousObject.value,
        arrivalTime: object.value,
        transit: previousObject.transit,
      };
      combinedArray.pop();
      combinedArray.push(combinedObject);
    }
    previousObject = object;
  });
  return combinedArray;
}

// Format data for the schedule column (data transit points)
const formatScheduleArray = (schedule: any) => {
  let scheduleArray = [];
  scheduleArray.push({
    label: "Pickup date",
    value: schedule?.quoted_pickup_date,
  });

  if (schedule?.flights && schedule?.flights?.length > 0) {
    schedule.flights.forEach((flight: any) => {
      if (
        flight.airline ||
        flight.flight_number ||
        flight.departure_airport ||
        flight.arrival_airport ||
        flight.departure ||
        flight.arrival
      )
        scheduleArray.push({
          label: `${flight.departure_airport}`,
          value: flight.departure,
          transit: "Departure",
        });
      scheduleArray.push({
        label: `${flight.arrival_airport}`,
        value: flight.arrival,
        transit: "Arrival",
      });
    });
    scheduleArray = formatFlightTransits(scheduleArray);
  }

  if (schedule?.sailing) {
    if (
      schedule.sailing.departure_port_name ||
      schedule.sailing.arrival_port_name ||
      schedule.sailing.arrival ||
      schedule.sailing.departure
    ) {
      scheduleArray.push({
        label: `Departure port: ${schedule.sailing.departure_port_name || "No port available"}`,
        value: schedule.sailing.departure,
      });
      scheduleArray.push({
        label: `Arrival port: ${schedule.sailing.arrival_port_name || "No port available"}`,
        value: schedule.sailing.arrival,
      });
    }
  }
  scheduleArray.push({
    label: "Delivery date",
    value: schedule?.quoted_time_delivery_date,
  });
  return scheduleArray;
};

const getQuotationListColumn = (intl: IntlShape, refetchQuotationLists: Function, shipmentData: IShipmentData) => {
  const columns = [
    {
      Header: intl.formatMessage({ id: "general.quoted", defaultMessage: "Quoted" }),
      accessor: (row: any) => [row?.provider?.name, row?.provider?.team, row?.provider?.created_by],
      Cell: ({ value, row }: any) => (
        <div>
          <div className="font-b9 mb-2">{value[0]}</div>
          <div className="font-b2">
            {value[1]}
            {row.original.created_by_user_type === "system" && !row.original.general.is_rate_card_quote && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="freight-mode">
                    <span className="font-weight-normal">API quote</span>
                  </Tooltip>
                }
              >
                <ApiIcon className="ml-2" />
              </OverlayTrigger>
            )}
          </div>
          <div className="font-b2 mt-2">{value[2]}</div>
          <div>
            {row?.original?.general?.is_rate_card_quote && (
              <div className="mt-2">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="freight-mode">
                      <span className="font-weight-normal">Rate card quote</span>
                    </Tooltip>
                  }
                >
                  <RateCardIcon className="mb-1 mr-1" />
                </OverlayTrigger>
                <span className="text-secondary">{row.original.general.quote_title}</span>
              </div>
            )}
          </div>
        </div>
      ),
      disableDrag: true,
    },
    {
      Header: intl.formatMessage({ id: "general.quote", defaultMessage: "Quote" }),
      minWidth: 150,
      accessor: (row: any) => ({
        total_quoted_amount: row?.financial?.total_quoted_amount,
        total_quoted_amount_currency: row?.financial?.total_quoted_amount_currency,
        is_highest_price: row?.financial?.is_highest_price,
        is_lowest_price: row?.financial?.is_lowest_price,
        not_included_in_price: row?.financial?.not_included_in_price,
        client_price_per_mile: row?.financial?.client_price_per_mile,
        client_price_per_km: row?.financial?.client_price_per_km,
        traffic_light_status: row?.traffic_light_status,
      }),
      Cell: ({ value }: any) => {
        const trafficLightColor = (() => {
          if (!value.traffic_light_status) return null;
          if (value.traffic_light_status === "green") return "bg-mustard-yellow";
          if (value.traffic_light_status === "amber") return "bg-mustard-yellow";
          if (value.traffic_light_status === "red") return "bg-peach-red";
        })();
        return (
          <>
            <div className="d-flex align-items-center">
              {value.traffic_light_status && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Popover id="traffic-light-tooltip">
                      <Popover.Content>
                        <div className="font-b3">
                          <FormattedMessage
                            id="trafficLight.greenInfo"
                            defaultMessage="Green = Below quoted average lower margin"
                          />
                          <br />
                          <FormattedMessage
                            id="trafficLight.amberInfo"
                            defaultMessage="Amber = Within quoted average margin"
                          />
                          <br />
                          <FormattedMessage
                            id="trafficLight.redInfo"
                            defaultMessage="Red = Above quoted average upper margin"
                          />
                        </div>
                      </Popover.Content>
                    </Popover>
                  }
                >
                  <div className={`traffic-light-info-container font-b4 ${trafficLightColor}`}>i</div>
                </OverlayTrigger>
              )}

              <div style={{ marginRight: "4px", fontSize: "11px" }}>
                {value.total_quoted_amount
                  ? `${formatPriceV2(value.total_quoted_amount)} ${value.total_quoted_amount_currency} `
                  : "-"}
                {value.not_included_in_price && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="freight-mode">
                        <span className="font-weight-bold">
                          <FormattedMessage id="quote-request.costDetailNotInclude" defaultMessage="Not included" />
                        </span>
                        <br /> {value.not_included_in_price}
                      </Tooltip>
                    }
                  >
                    <WarningIcon className="mb-1 not-included-icon" />
                  </OverlayTrigger>
                )}
              </div>
              <div>
                {value.is_highest_price && (
                  <Badge
                    type="warning"
                    label={intl.formatMessage({
                      id: "general.highest",
                      defaultMessage: "Highest",
                    })}
                  />
                )}
                {value.is_lowest_price && (
                  <Badge
                    type="primary"
                    label={intl.formatMessage({
                      id: "general.lowest",
                      defaultMessage: "Lowest",
                    })}
                  />
                )}
              </div>
            </div>
            {value.client_price_per_mile && value.client_price_per_km && (
              <div className="font-b3">
                <span className="text-secondary">{`${formatPriceV2(value.client_price_per_mile)} ${
                  value.total_quoted_amount_currency
                } /mi | ${formatPriceV2(value.client_price_per_km)} ${value.total_quoted_amount_currency} /km`}</span>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="freight-mode">
                      <span className="font-weight-bold">
                        <FormattedMessage
                          id="quote-request.roadFreightCosts"
                          defaultMessage="Total quoted amount, divided by the estimated distance in miles and km."
                        />
                        <br />
                        <FormattedMessage id="quote-request.experimentalFeature" defaultMessage="Experimental feature" />
                      </span>
                      <br /> {value.not_included_in_price}
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faQuestionCircle} className="ml-2" />
                </OverlayTrigger>
              </div>
            )}
          </>
        );
      },
      disableDrag: true,
    },
    {
      Header: intl.formatMessage({ id: "general.schedule", defaultMessage: "Schedule" }),
      accessor: "test",
      Cell: ({ row }: any) => {
        const deliveryDatetime = row?.original?.schedule?.quoted_time_delivery_date;
        const pickupDatetime = row?.original?.schedule?.quoted_pickup_date;
        const earliestDelivery = row?.original?.schedule?.earliest_delivery;
        const earliestPickup = row?.original?.schedule?.earliest_pickup;
        const shortestTransit = row?.original?.schedule?.shortest_transit_time;
        const transitTime = row?.original?.schedule?.quoted_transit_time;

        const dataPoints = formatScheduleArray(row?.original?.schedule);

        let lowestOnTime: any = null;
        if (row?.original?.schedule?.flights) {
          row.original.schedule.flights.forEach((flight: any) => {
            const onTime = parseFloat(flight.performance.on_time);
            if (onTime && (lowestOnTime === null || onTime < lowestOnTime)) {
              lowestOnTime = onTime;
            }
          });
        }

        const Icon = getFreightModeIcon(
          row?.original?.service?.freight_mode_label,
          row?.original?.service?.freight_mode_type,
          true,
        );
        return (
          <div>
            <div>
              <div className="d-flex">
                <div style={{ fontSize: "11px" }}>
                  <div className="text-secondary">Pickup</div>
                  {pickupDatetime === null ? "-" : <div>{formatDatetime(pickupDatetime)}</div>}
                </div>
                <div
                  className="d-flex flex-column justify-content-center align-items-center"
                  style={{
                    margin: "0 18px 0 18px",
                  }}
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="freight-mode">
                        <span className="font-weight-normal">{row?.original?.service?.freight_mode_label}</span>
                      </Tooltip>
                    }
                  >
                    <Icon
                      style={{
                        height: "18px",
                      }}
                    />
                  </OverlayTrigger>
                  <div className="font-weight-bold" style={{ fontSize: "9px" }}>
                    {deliveryDatetime === null || pickupDatetime === null ? "" : <span>{transitTime}</span>}
                  </div>
                </div>
                <div style={{ fontSize: "11px" }}>
                  <div className="text-secondary">Delivery</div>
                  {deliveryDatetime === null ? "Not specified" : <div>{formatDatetime(deliveryDatetime)}</div>}
                </div>
              </div>
            </div>
            <div>
              <div className="d-flex justify-content-between position-relative" style={{ width: "190px" }}>
                <div
                  style={{
                    width: `190px`,
                    height: "2px",
                    borderBottom: `2px dotted ${renderColor(lowestOnTime)}`,
                    position: "absolute",
                    top: 3,
                  }}
                />
                {dataPoints.map((point: any) => (
                  <div className="d-flex align-items-center" key={point.value}>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id="freight-mode">
                          <span className="font-weight-normal">
                            {point.label !== "" && point.label !== "null" ? point.label : "Not available"}
                          </span>
                          <br />
                          {point.value && (
                            <span className="font-weight-normal">
                              {point.transit && `${point.transit}: `} {formatDatetime(point.value)}
                            </span>
                          )}
                          {point.departureTime && (
                            <div className="font-weight-normal">{`Arrival: ${formatDatetime(point.arrivalTime)}`}</div>
                          )}
                          {point.arrivalTime && (
                            <div className="font-weight-normal">{`Departure: ${formatDatetime(point.departureTime)}`}</div>
                          )}
                        </Tooltip>
                      }
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "white",
                          border: "2px solid black",
                          zIndex: 10,
                        }}
                      />
                    </OverlayTrigger>
                  </div>
                ))}
              </div>
              <div
                style={{ width: "190px" }}
                className="font-b9 d-flex flex-column align-items-center justify-content-center mt-1"
              >
                {earliestPickup && <div>Earliest Pickup</div>}
                {earliestDelivery && <div>Earliest delivery</div>}
                {shortestTransit && <div>Shortest transit</div>}
              </div>
            </div>
          </div>
        );
      },
      disableDrag: true,
    },
    {
      Header: intl.formatMessage({ id: "general.status", defaultMessage: "Status" }),
      accessor: (row: any) => [row.schedule.quoted_pickup_date, row.schedule.quoted_time_delivery_date],
      Cell: ({ row }: any) => {
        const quotesData = row.original;
        return <QuotationStatus quotesData={quotesData} />;
      },
      disableDrag: true,
    },
    {
      Header: intl.formatMessage({ id: "general.files", defaultMessage: "Files" }),
      accessor: (row: any) => [row?.other?.attachments],
      Cell: ({ value }: any) => {
        const ref = useRef<HTMLDivElement>(null);
        const [show, setShow] = useState(false);

        useEffect(() => {
          const checkIfClickedOutside = (e: any) => {
            if (show && ref?.current && !ref?.current.contains(e.target)) {
              setShow(false);
            }
          };
          document.addEventListener("mousedown", checkIfClickedOutside);
          return () => {
            document.removeEventListener("mousedown", checkIfClickedOutside);
          };
        }, [show]);

        const handleShowMenu = (e: any) => {
          e.stopPropagation();
          setShow(!show);
        };

        return (
          <div className="position-relative">
            {value[0]?.length > 0 && <FolderIcon onClick={handleShowMenu} />}
            {show && (
              <div
                ref={ref}
                className="d-flex flex-column justify-content-center border-grey folder-tooltip"
                style={{ width: "191px", height: "auto", padding: "16px 0px" }}
              >
                {value && value[0]?.length > 0 ? (
                  value[0].map((file: any) => (
                    <div
                      className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                      id="new-quick-request"
                      role="presentation"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      style={{ minHeight: "40px", paddingLeft: "7px", paddingRight: "7px", maxWidth: "100%" }}
                    >
                      <span>
                        <FileIcon className="ml-2" height="24" width="24" />
                      </span>
                      <span className="font-b2 ml-3 filename-span">
                        <a target="_blank" href={file.file_url} rel="noreferrer">
                          {file.filename}
                        </a>
                      </span>
                    </div>
                  ))
                ) : (
                  <span className="font-b2 ml-5">No attachments found</span>
                )}
              </div>
            )}
          </div>
        );
      },
      disableDrag: true,
    },
    {
      Header: intl.formatMessage({ id: "general.action", defaultMessage: "Action" }),
      accessor: (row: any) => [row],
      Cell: ({ row }: any) => {
        const { related_shipments } = shipmentData || {};
        if (!related_shipments?.length || related_shipments[0]?.relation === "Child") {
          return <QuotationAction reference={row.original} refetchQuotationLists={refetchQuotationLists} />;
        }
      },
      disableDrag: true,
    },
    {
      Header: () => null,
      id: "expander",
      Cell: ({ row }: any) => (
        <div className="d-flex align-items-center position-relative">{row.isExpanded ? <ArrowUp /> : <ArrowDown />}</div>
      ),
      disableDrag: true,
    },
  ];
  return columns;
};

export default getQuotationListColumn;
